import React, { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { Text } from '@paper-monster/web/src/components/common'

import styles from './HowItWorks.module.scss'

function RevealImage({ alt, height, src, width }) {
  const imageRef = useRef(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const imageElement = imageRef.current
    if (!imageElement || !('IntersectionObserver' in window)) {
      setIsVisible(true)
      return undefined
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.unobserve(entry.target)
        }
      },
      { threshold: 0.2 },
    )

    observer.observe(imageElement)

    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={imageRef}
      className={cx(styles.imagePanel, isVisible && styles.imageVisible)}
    >
      <Image alt={alt} className={styles.image} height={height} src={src} width={width} />
    </div>
  )
}

RevealImage.propTypes = {
  alt: PropTypes.string.isRequired,
  height: PropTypes.number.isRequired,
  src: PropTypes.string.isRequired,
  width: PropTypes.number.isRequired,
}

export default function HowItWorks({ t }) {
  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <div className={styles.header}>
          <Text className={styles.label}>{t.label}</Text>
          <Text.H2 className={styles.sectionTitle}>{t.title}</Text.H2>
          <Text.P className={styles.sectionDescription}>{t.subtitle}</Text.P>
        </div>
        {t.slides.map((slide, index) => (
          <article key={slide.title} className={cx(styles.step, index === 1 && styles.reverse)}>
            <div className={styles.content}>
              <Text className={styles.label}>{slide.eyebrow}</Text>
              <Text.H3 className={styles.title}>{slide.title}</Text.H3>
              <Text.P className={styles.description}>{slide.description}</Text.P>
              <ul className={styles.bullets}>
                {slide.bullets.map((item) => (
                  <li key={item} className={styles.bulletItem}>
                    <Image alt="" className={styles.checkIcon} height={20} src="/green-check.png" width={20} />
                    <Text>{item}</Text>
                  </li>
                ))}
              </ul>
            </div>
            <RevealImage alt={slide.alt} height={slide.height} src={slide.image} width={slide.width} />
          </article>
        ))}
      </div>
    </section>
  )
}

HowItWorks.propTypes = {
  t: PropTypes.shape({
    label: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    slides: PropTypes.arrayOf(
      PropTypes.shape({
        eyebrow: PropTypes.string.isRequired,
        image: PropTypes.string.isRequired,
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
        alt: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        bullets: PropTypes.arrayOf(PropTypes.string).isRequired,
      }),
    ).isRequired,
  }).isRequired,
}
